import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

// Initialize EmailJS with your public key
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

const Contact = () => {
  const [formData, setFormData] = useState({
    from_name: '',
    phone: '',
    inquiryType: 'retail',
    message: '',
    to_name: 'ANG Team'
  });

  const [submitStatus, setSubmitStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const formRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitStatus({ isSubmitting: true, isSubmitted: false, error: null });

      // Format inquiry type for better email subject
      const formattedInquiryType = {
        retail: 'Retail Purchase',
        wholesale: 'Wholesale',
        custom: 'Custom Order',
        other: 'General'
      }[formData.inquiryType];

      // Prepare template parameters
      const templateParams = {
        ...formData,
        inquiryType: formattedInquiryType
      };

      const result = await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setSubmitStatus({ isSubmitting: false, isSubmitted: true, error: null });
        // Reset form
        setFormData({
          from_name: '',
          phone: '',
          inquiryType: 'retail',
          message: '',
          to_name: 'ANG Team'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({ 
        isSubmitting: false, 
        isSubmitted: false, 
        error: `Failed to send message: ${error.message || 'Please try again later.'}`
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGetDirections = () => {
    window.open('https://www.google.com/maps/place/ANG+GOOD+ESSENTIALS+LLP/@26.816328,75.5587333,17z/data=!3m1!4b1!4m6!3m5!1s0x396db5ac01f368a9:0x23c05558b114cf1d!8m2!3d26.8163233!4d75.5636042!16s%2Fg%2F11fn4p013c', '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F6F2]">
      <main className="flex-grow">
        <section className="pt-20 sm:pt-24 md:pt-32">
          <div className="container mx-auto px-4 sm:px-6 mb-8 sm:mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-2xl"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#2C231E] mb-4 sm:mb-6 leading-tight">
                LET'S CONNECT
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-[#2C231E]/70 font-lora font-light leading-relaxed">
                Architects. Designers. Retailers. Let's Talk. Whether you're looking to place an order, discuss a custom project,
                or explore wholesale opportunities, we're here to help bring your vision to life.
              </p>
            </motion.div>
          </div>

          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-stretch">
              <motion.div 
                className="relative overflow-hidden h-full"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {/* Send a Message Section */}
                <div className="relative bg-white/90 p-6 sm:p-8 md:p-12 rounded-2xl shadow-xl border border-[#B38053]/10 h-full">
                  <div className="absolute -top-16 -left-16 w-48 h-48 bg-[#B38053]/5 rounded-full opacity-50"></div>
                  <div className="absolute -bottom-20 -right-10 w-64 h-64 bg-[#B38053]/5 rounded-full opacity-60"></div>
                  
                  <div className="h-full flex flex-col">
                    <h2 className="text-2xl sm:text-3xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">SEND A MESSAGE</h2>
                    <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed mb-6 sm:mb-8 relative z-10">
                      Have a question about our products or interested in placing an order? Fill out the form below and we'll get back to you shortly.
                    </p>

                    <form ref={formRef} onSubmit={handleSubmit} className="relative z-10 space-y-4 sm:space-y-6 flex-grow">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-medium font-lora text-[#2C231E] mb-1" htmlFor="from_name">
                            Your Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#B38053]">
                              person_outline
                            </span>
                            <input
                              type="text"
                              id="from_name"
                              name="from_name"
                              value={formData.from_name}
                              onChange={handleChange}
                              required
                              placeholder="e.g., Jane Doe"
                              className="mt-1 block w-full px-4 py-3 pl-10 bg-white/90 border border-[#B38053]/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B38053] focus:border-[#B38053] text-base sm:text-sm font-lora text-[#2C231E] placeholder-[#2C231E]/30 transition-all duration-300"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium font-lora text-[#2C231E] mb-1" htmlFor="phone">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#B38053]">
                              phone
                            </span>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              placeholder="e.g., +91 9876543210"
                              className="mt-1 block w-full px-4 py-3 pl-10 bg-white/90 border border-[#B38053]/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B38053] focus:border-[#B38053] text-base sm:text-sm font-lora text-[#2C231E] placeholder-[#2C231E]/30 transition-all duration-300"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium font-lora text-[#2C231E] mb-1" htmlFor="inquiryType">
                          Inquiry Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#B38053]">
                            category
                          </span>
                          <select
                            id="inquiryType"
                            name="inquiryType"
                            value={formData.inquiryType}
                            onChange={handleChange}
                            required
                            className="mt-1 block w-full px-4 py-3 pl-10 bg-white/90 border border-[#B38053]/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B38053] focus:border-[#B38053] text-base sm:text-sm font-lora text-[#2C231E] placeholder-[#2C231E]/30 transition-all duration-300 appearance-none"
                          >
                            <option value="retail">Retail Purchase</option>
                            <option value="wholesale">Wholesale</option>
                            <option value="custom">Custom Order</option>
                            <option value="other">Other</option>
                          </select>
                          <span className="material-icons-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[#B38053] pointer-events-none">
                            expand_more
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium font-lora text-[#2C231E] mb-1" htmlFor="message">
                          Your Message <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="material-icons-outlined absolute left-3 top-4 text-[#B38053]">
                            chat_bubble_outline
                          </span>
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows="5"
                            required
                            placeholder="Tell us about your project or question..."
                            className="mt-1 block w-full px-4 py-3 pl-10 bg-white/90 border border-[#B38053]/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B38053] focus:border-[#B38053] text-base sm:text-sm font-lora text-[#2C231E] placeholder-[#2C231E]/30 transition-all duration-300 min-h-[120px] resize-y"
                          ></textarea>
                        </div>
                      </div>

                      {submitStatus.error && (
                        <div className="text-red-500 text-sm font-lora bg-white/90 p-3 rounded-lg border border-red-200">
                          {submitStatus.error}
                        </div>
                      )}

                      {submitStatus.isSubmitted && (
                        <div className="text-green-600 text-sm font-lora bg-white/90 p-3 rounded-lg border border-green-200">
                          Message sent successfully!
                        </div>
                      )}

                      <div>
                        <button
                          type="submit"
                          disabled={submitStatus.isSubmitting}
                          className="w-full flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-[#B38053] rounded-lg text-base font-lora text-white bg-[#B38053] hover:bg-[#96663D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B38053] transition duration-300 ease-in-out transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
                        >
                          {submitStatus.isSubmitting ? (
                            <span className="flex items-center">
                              <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                              Sending...
                            </span>
                          ) : (
                            <>
                              <span className="material-icons mr-2 group-hover:animate-ping-once">send</span>
                              Send Your Message
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="relative h-full"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {/* Visit Our Workshop Section */}
                <div className="relative bg-white/90 p-6 sm:p-8 md:p-12 rounded-2xl shadow-xl border border-[#B38053]/10 h-full">
                  <div className="absolute -top-16 -left-16 w-48 h-48 bg-[#B38053]/5 rounded-full opacity-50"></div>
                  <div className="absolute -bottom-20 -right-10 w-64 h-64 bg-[#B38053]/5 rounded-full opacity-60"></div>

                  <div className="h-full flex flex-col">
                    <h2 className="text-2xl sm:text-3xl font-playfair font-light text-[#2C231E] mb-3 sm:mb-4">VISIT OUR FACTORY</h2>
                    <p className="text-sm sm:text-base text-[#2C231E]/70 font-lora font-light leading-relaxed mb-6 sm:mb-8 relative z-10">
                      Experience our craftsmanship firsthand at our factory in Bagru, where traditional techniques meet modern design.
                    </p>
                    <div className="space-y-4 sm:space-y-6 flex-grow relative z-10">
                      <div className="flex items-start gap-3">
                        <span className="material-icons text-[#B38053] mt-1">location_on</span>
                        <div>
                          <p className="font-lora text-[#2C231E]">65, Solitaire Industrial Park</p>
                          <p className="font-lora text-[#2C231E]">Bagru, Rajasthan, 303007</p>
                        </div>
                      </div>
                      <a 
                        href="tel:+919694444893"
                        className="flex items-center gap-3 text-[#2C231E] hover:text-[#B38053] transition-colors duration-300"
                      >
                        <span className="material-icons text-[#B38053]">phone</span>
                        <span>+91 9694444893</span>
                      </a>
                      <a 
                        href="mailto:anggood.info@gmail.com"
                        className="flex items-center gap-3 text-[#2C231E] hover:text-[#B38053] transition-colors duration-300"
                      >
                        <span className="material-icons text-[#B38053]">email</span>
                        <span>anggood.info@gmail.com</span>
                      </a>
                    </div>
                    <button
                      onClick={handleGetDirections}
                      className="mt-6 sm:mt-8 w-full flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-[#B38053] rounded-lg text-base font-lora text-white bg-[#B38053] hover:bg-[#96663D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B38053] transition duration-300 ease-in-out transform hover:scale-[1.02] active:scale-[0.98] relative z-10"
                    >
                      <span className="material-icons mr-2">directions</span>
                      Get Directions
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact;
